import { Component, Input, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { NgSelectModule } from '@ng-select/ng-select';
import { Observable } from 'rxjs';
import { finalize } from 'rxjs/operators';

export interface FileModalData {
    id?: string;
    title?: string;
    descr?: string;
    categoryId?: string | null;
    realFilename?: string;
}

/** What `save()` emits — the form values plus the chosen file, if any. */
export type FileSavePayload = FileModalData & {
    realFilename?: string;
    contentType?: string;
    fileBase64?: string;
};

export interface CategoryOption {
    id: string;
    categoryName: string;
}

/**
 * Add / edit file modal. On add a file must be chosen; on edit the file is
 * optional (choose one only to replace). The file is read to a base64 data URI
 * and sent in the payload.
 *
 * The caller supplies `saveHandler` with the request to run. The modal stays
 * open while it is in flight — uploads are slow enough (the file travels as
 * base64 in the body) that closing on click would leave the user with no idea
 * whether it worked — and closes only once it succeeds. The caller still owns
 * the toast and the list reload, via the modal result.
 */
@Component({
    selector: 'app-file-modal',
    imports: [FormsModule, NgSelectModule],
    templateUrl: './file-modal.component.html',
    styleUrl: './file-modal.component.scss',
})
export class FileModalComponent implements OnInit {
    @Input() file: FileModalData | null = null;
    @Input() categories: CategoryOption[] = [];
    /**
     * Performs the create/update. Omit it and the modal closes immediately with
     * the payload, leaving the request to the caller (the old behaviour).
     */
    @Input() saveHandler?: (payload: FileSavePayload) => Observable<any>;

    id?: string;
    title = '';
    descr = '';
    categoryId: string | null = null;
    existingFilename = '';

    // Newly chosen file (only when replacing/uploading).
    realFilename = '';
    contentType = '';
    fileBase64 = '';

    error: string | null = null;
    /** True while the upload request is in flight. */
    submitting = false;
    titleInvalid = false;
    fileInvalid = false;
    shaking = false;

    constructor(public activeModal: NgbActiveModal) { }

    get modalTitle(): string {
        return this.id ? 'Edit file' : 'Add file';
    }

    get isEdit(): boolean {
        return !!this.id;
    }

    ngOnInit(): void {
        if (this.file) {
            this.id = this.file.id;
            this.title = this.file.title || '';
            this.descr = this.file.descr || '';
            this.categoryId = this.file.categoryId ?? null;
            this.existingFilename = this.file.realFilename || '';
        }
    }

    onFileSelected(event: Event): void {
        const input = event.target as HTMLInputElement;
        const file = input.files && input.files[0];
        if (!file) return;
        this.realFilename = file.name;
        this.contentType = file.type || 'application/octet-stream';
        const reader = new FileReader();
        reader.onload = () => { this.fileBase64 = String(reader.result || ''); };
        reader.readAsDataURL(file);
    }

    save(): void {
        if (this.submitting) return;
        this.error = null;
        this.titleInvalid = false;
        this.fileInvalid = false;
        let bad = false;

        if (!this.title.trim()) {
            this.titleInvalid = true;
            this.error = 'Title is required';
            bad = true;
        }
        if (!this.isEdit && !this.fileBase64) {
            this.fileInvalid = true;
            this.error = 'Please choose a file';
            bad = true;
        }
        if (bad) {
            this.triggerShake();
            return;
        }

        const payload: FileSavePayload = {
            id: this.id,
            title: this.title.trim(),
            descr: this.descr.trim(),
            categoryId: this.categoryId || '',
        };
        if (this.fileBase64) {
            payload.fileBase64 = this.fileBase64;
            payload.realFilename = this.realFilename;
            payload.contentType = this.contentType;
        }

        if (!this.saveHandler) {
            this.activeModal.close(payload);
            return;
        }

        this.submitting = true;
        this.saveHandler(payload)
            .pipe(finalize(() => { this.submitting = false; }))
            .subscribe({
                next: () => this.activeModal.close(payload),
                // Stay open so the user can retry without re-entering everything.
                error: () => {
                    this.error = this.isEdit
                        ? 'Unable to update the file. Please try again.'
                        : 'Unable to upload the file. Please try again.';
                },
            });
    }

    private triggerShake(): void {
        this.shaking = true;
        setTimeout(() => { this.shaking = false; }, 400);
    }
}
